import { formatDateTime } from "@/api/datetime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSmsHistory } from "@/features/sms/hooks/useSmsHistory";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { functions } from "@/firebase";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  MessageSquare,
  Phone,
  RefreshCw,
  RotateCw,
  User,
  XCircle,
} from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/dashboard/admin/sms_history/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, refetch } = useSmsHistory(50);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retrySuccess, setRetrySuccess] = useState<string | null>(null);
  const { workers } = useAllWorkers();

  const messages = data?.pages.flatMap((page) => page.messages) ?? [];

  const handleRetry = async (smsId: string) => {
    setRetryingId(smsId);
    setRetryError(null);
    setRetrySuccess(null);

    try {
      const retrySmsCallable = httpsCallable(functions, "retrySmsCallable");
      const result = await retrySmsCallable({ smsId });
      const data = result.data as { success: boolean; error?: string; messageId?: string };

      if (data.success) {
        setRetrySuccess(`Message retried successfully!`);
        // Refresh the list after a short delay to show the updated status
        setTimeout(() => {
          refetch();
          setRetrySuccess(null);
        }, 2000);
      } else {
        setRetryError(data.error || "Failed to retry message");
      }
    } catch (error) {
      console.error("Error retrying SMS:", error);
      setRetryError(error instanceof Error ? error.message : "Failed to retry message");
    } finally {
      setRetryingId(null);
    }
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    return formatDateTime(timestamp);
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "sent":
      case "delivered":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed":
      case "undelivered":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusVariant = (
    status: string,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status.toLowerCase()) {
      case "sent":
      case "delivered":
        return "default";
      case "failed":
      case "undelivered":
        return "destructive";
      case "pending":
        return "secondary";
      default:
        return "outline";
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-1 sm:p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">SMS History</h1>
            <p className="text-muted-foreground">Message delivery logs and status</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="mb-3 h-16 w-full" />
                <Skeleton className="h-3 w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-1 sm:p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SMS History</h1>
          <p className="text-muted-foreground">Message delivery logs and status</p>
        </div>
        <Button
          onClick={() => refetch()}
          disabled={isFetching}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {retrySuccess && (
        <div className="mb-4 rounded-lg bg-green-50 p-4 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-400">
          {retrySuccess}
        </div>
      )}

      {retryError && (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{retryError}</span>
          </div>
        </div>
      )}

      {messages.length === 0 ? (
        <div className="py-8 text-center">
          <MessageSquare className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
          <p className="text-muted-foreground">No SMS messages found</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {messages.map((message, index) => (
              <Card key={`${message.id}-${index}`} className="transition-shadow hover:shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      {message.workerId ? (
                        <Link
                          to="/dashboard/admin/workers/$workerId"
                          params={{ workerId: message.workerId }}
                          className="text-primary mb-2 flex items-center gap-2 hover:underline"
                        >
                          <User className="text-muted-foreground h-4 w-4" />
                          <span className="truncate text-sm font-medium">
                            {workers.find((w) => w.id === message.workerId)?.name ||
                              "Unknown Worker"}
                          </span>
                        </Link>
                      ) : (
                        <div className="mb-2 text-sm font-medium"></div>
                      )}
                      <div className="flex items-center gap-2">
                        <Phone className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                        <span className="truncate text-sm font-medium">
                          {formatPhoneNumber(message.phoneNumber)}
                        </span>
                      </div>
                    </div>
                    <Badge
                      variant={getStatusVariant(message.status)}
                      className="ml-2 flex flex-shrink-0 items-center gap-1"
                    >
                      {getStatusIcon(message.status)}
                      {message.status}
                    </Badge>
                  </div>
                  <div className="text-muted-foreground flex items-center gap-1 text-xs">
                    <Clock className="h-3 w-3" />
                    <span>{formatTimestamp(message.createdAt)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 mb-3 rounded-lg p-3">
                    <p className="text-sm leading-relaxed break-words">{message.message}</p>
                  </div>

                  {message.error && (
                    <div className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      <span className="font-medium">Error: </span>
                      <span className="line-clamp-2">{message.error}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2">
                    <div className="space-y-1">
                      {message.updatedAt && message.updatedAt !== message.createdAt && (
                        <div className="text-muted-foreground text-xs">
                          Updated {formatTimestamp(message.updatedAt)}
                        </div>
                      )}
                      {message.retryCount !== undefined && message.retryCount > 0 && (
                        <div className="text-muted-foreground text-xs">
                          Retry attempts: {message.retryCount} / 5
                        </div>
                      )}
                    </div>

                    {message.status === "failed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRetry(message.id)}
                        disabled={retryingId === message.id || (message.retryCount || 0) >= 5}
                        className="flex items-center gap-1"
                      >
                        <RotateCw
                          className={`h-3 w-3 ${retryingId === message.id ? "animate-spin" : ""}`}
                        />
                        {retryingId === message.id ? "Retrying..." : "Retry"}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetching}
                variant="outline"
                className="flex items-center gap-2"
              >
                {isFetching ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
